import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import { Small } from 'foundations-components/transitional/components/Small';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import Raven from 'raven-js';
import { useMemo } from 'react';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { useMonthlyCalculatedPrice } from '../../../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { planBuilderCommonTermForProductConfigurationsSelector } from '../../../selectors/PlanBuilderSelectors';
import { billingNameKeySelector, includedQuantityBySkuIdSelector, isBreezeIntelligenceSkuSelector, productForSkuIdSelector, productVersionSelector, unitNameForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../../selectors/PurchaseMotionSelectors';
import { AddOnCardPricingFallback } from '../../available-add-ons-list/AddOnCardFallbacks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ExpandableChecklistItem = ({
  skuId,
  selectedQuantity,
  onSelectedChange,
  use
}) => {
  const commonTermForProductConfigurations = useAppSelector(planBuilderCommonTermForProductConfigurationsSelector);
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const apiName = product === null || product === void 0 ? void 0 : product.apiName;
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const isRecurring = useAppSelector(state => {
    const {
      chargeType
    } = productForSkuIdSelector(state, {
      skuId
    });
    return chargeType === RECURRING;
  });
  const quantifiable = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.quantifiable;
  });
  const {
    monthlyCalculatedPrice
  } = useMonthlyCalculatedPrice({
    apiName,
    queryParamTerm: commonTermForProductConfigurations
  });
  const singleUnitPrice = useMemo(() => {
    if (monthlyCalculatedPrice && isRecurring) {
      return monthlyCalculatedPrice * (selectedQuantity || 1);
    }
    if (!isRecurring) {
      Raven.captureException(new Error('AddOnCardPricing: Attempting to show one-time fee pricing for product with pricing API enabled. One-time fees may need to be handled differently'));
    }
  }, [isRecurring, monthlyCalculatedPrice, selectedQuantity]);
  const isTouchlessEligible = useAppSelector(state => purchaseMotionsSelector(state, {
    location: 'CALCULATOR',
    skuId
  }).includes(BUY_TOUCHLESS));
  const maxTouchlessPurchasableQuantity = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.maxTouchlessPurchasableQuantity;
  });
  const isBreezeProduct = useAppSelector(state => isBreezeIntelligenceSkuSelector(state, {
    skuId
  }));
  const unitName = useAppSelector(state => unitNameForSkuIdSelector(state, {
    skuId
  }));
  const includedUnits = useAppSelector(state => unitName ? includedQuantityBySkuIdSelector(state, {
    skuId,
    unitName
  }) : 1);
  const minValue = includedUnits;
  const maxValue = maxTouchlessPurchasableQuantity === null ? Infinity : includedUnits * maxTouchlessPurchasableQuantity;
  const canBuyMultiple = quantifiable && maxValue > 1;
  const checked = selectedQuantity > 0;
  const handleStepperChange = evt => {
    const newQuantity = Math.round(parseInt(evt.target.value, 10) / includedUnits);
    if (isNaN(newQuantity)) return;
    onSelectedChange({
      value: newQuantity,
      skuId,
      apiName,
      previousValue: selectedQuantity
    });
  };
  return /*#__PURE__*/_jsx(SelectableBox, {
    textAlign: "left",
    type: "checkbox",
    selectionMarkLocation: "top-right",
    selected: checked,
    onSelectedChange: () => {
      onSelectedChange({
        value: checked ? 0 : 1,
        skuId,
        apiName,
        previousValue: selectedQuantity
      });
    },
    "data-selenium-class": `${use}-checklist-item`,
    "data-selected": checked,
    "data-touchless-eligible": isTouchlessEligible,
    "data-api-name": apiName,
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        gap: "200",
        justifyContent: "space-between",
        inlineSize: "100%",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexDirection: "row",
            justifyContent: "start",
            alignItems: "center",
            gap: "200",
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `pricing-pages.products.${billingNameKey}.title`
              })
            })
          }), singleUnitPrice !== undefined ? /*#__PURE__*/_jsxs(Text, {
            typography: "body-125",
            children: [/*#__PURE__*/_jsx(PriceDisplay, {
              price: singleUnitPrice,
              currencyCode: currencyCode
            }), isRecurring ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.priceDisplay.perMonth"
            }) : /*#__PURE__*/_jsx(Text, {
              marginInlineStart: "100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "pricingPage.notes.oneTimeFeeParentheses"
              })
            })]
          }) : /*#__PURE__*/_jsx(AddOnCardPricingFallback, {}), isBreezeProduct && /*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.addOns.cards.breezeIntelligence.subtitle"
            })
          })]
        }), canBuyMultiple && /*#__PURE__*/_jsx(View, {
          marginInlineStart: "400",
          onClick: evt => {
            evt.stopPropagation();
          },
          children: /*#__PURE__*/_jsx(FormControl, {
            label: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("span", {
                className: "sr-only",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `pricing-pages.products.${billingNameKey}.title`
                })
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "planBuilder.productPages.quantity"
              })]
            }),
            children: /*#__PURE__*/_jsx(StepperInput, {
              inlineSize: "100px",
              "data-selenium-class": `${use}-stepper-input`,
              stepSize: includedUnits,
              onChange: handleStepperChange,
              value: selectedQuantity * includedUnits,
              min: minValue,
              max: maxValue
            })
          })
        })]
      }), /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `pricing-pages.products.${billingNameKey}.paragraph`
        })
      })]
    })
  });
};
export default ExpandableChecklistItem;